import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Calendar as CalendarIcon, Plus, Loader2, Users, List, LayoutGrid, ExternalLink, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { shiftApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface ScheduleFormData {
  name: string;
  start_date: string;
  end_date: string;
  is_published: boolean;
}

interface AssignmentFormData {
  schedule_id: string;
  shift_id: string;
  employee_id: string;
  work_date: string;
  status: string;
}

const ShiftSchedules: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<string | null>(null);
  
  // Date picker states
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [workDate, setWorkDate] = useState<Date | undefined>(undefined);
  
  const [scheduleForm, setScheduleForm] = useState<ScheduleFormData>({
    name: '',
    start_date: '',
    end_date: '',
    is_published: false,
  });
  const [assignmentForm, setAssignmentForm] = useState<AssignmentFormData>({
    schedule_id: '',
    shift_id: '',
    employee_id: '',
    work_date: '',
    status: 'SCHEDULED',
  });

  // Fetch schedules
  const { data: schedules = [], isLoading: schedulesLoading, error: schedulesError } = useQuery<any[]>({
    queryKey: ['schedules'],
    queryFn: async () => (await shiftApi.schedules.getAll({})) as any[],
  });

  // Fetch shifts
  const { data: shifts = [] } = useQuery<any[]>({
    queryKey: ['shifts'],
    queryFn: async () => (await shiftApi.shifts.getAll({})) as any[],
  });

  // Fetch employees
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ['employees'],
    queryFn: async () => (await employeeApi.getAll({})) as any[],
  });

  // Fetch assignments for selected schedule
  const { data: assignments = [] } = useQuery<any[]>({
    queryKey: ['shiftAssignments', selectedSchedule],
    queryFn: async () => (await shiftApi.assignments.getAll({ schedule_id: selectedSchedule })) as any[],
    enabled: !!selectedSchedule,
  });

  const createScheduleMutation = useMutation({
    mutationFn: shiftApi.schedules.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setIsScheduleDialogOpen(false);
      resetScheduleForm();
      toast({ title: 'Schedule created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const createAssignmentMutation = useMutation({
    mutationFn: shiftApi.assignments.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftAssignments'] });
      setIsAssignmentDialogOpen(false);
      resetAssignmentForm();
      toast({ title: 'Shift assigned successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleScheduleSubmit = () => {
    if (!scheduleForm.name || !startDate || !endDate) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    const submitData = {
      ...scheduleForm,
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
    };
    createScheduleMutation.mutate(submitData);
  };

  const handleAssignmentSubmit = () => {
    if (!assignmentForm.shift_id || !assignmentForm.employee_id || !workDate) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }
    const submitData = {
      ...assignmentForm,
      work_date: format(workDate, 'yyyy-MM-dd'),
    };
    createAssignmentMutation.mutate(submitData);
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      name: '',
      start_date: '',
      end_date: '',
      is_published: false,
    });
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      schedule_id: selectedSchedule || '',
      shift_id: '',
      employee_id: '',
      work_date: '',
      status: 'SCHEDULED',
    });
    setWorkDate(undefined);
  };

  const openAssignmentDialog = (scheduleId: string) => {
    setAssignmentForm({ ...assignmentForm, schedule_id: scheduleId });
    setIsAssignmentDialogOpen(true);
  };

  const getEmployeeName = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const getShiftName = (shiftId: string) => {
    const shift = shifts.find((s: any) => s.id === shiftId);
    return shift ? shift.name : 'Unknown';
  };

  const getStatusBadge = (status: string) => {
    const isPositive = ['ACTIVE', 'PUBLISHED', 'CONFIRMED', 'COMPLETED', 'SCHEDULED'].includes(status.toUpperCase());
    const classNames = isPositive 
      ? 'bg-emerald-50 text-emerald-705 border-emerald-250/50 dark:bg-emerald-950/20 dark:text-emerald-400' 
      : 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0 capitalize", classNames)} variant="outline">
        {status.toLowerCase()}
      </Badge>
    );
  };

  if (schedulesLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-650" />
        <span className="ml-2 mt-3 font-bold text-xs text-slate-400">Loading schedules...</span>
      </div>
    );
  }

  if (schedulesError) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-xs font-semibold m-6">
        <p className="font-bold text-sm">Error loading schedules</p>
        <p className="mt-1">{schedulesError.message}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-3.5">
          <Button
            onClick={() => navigate('/shift-planning')}
            variant="outline"
            size="icon"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Manage employee shift schedules and assignments</h2>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 self-start md:self-auto">
          <div className="flex items-center border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 p-0.5 shadow-sm overflow-hidden shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              className={cn(
                "rounded-lg font-bold text-xs h-8 px-3 transition-all",
                viewMode === 'list' 
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 hover:text-white dark:bg-indigo-500 dark:hover:bg-indigo-600" 
                  : "text-slate-500 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              )}
            >
              <List className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              List
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('calendar')}
              className={cn(
                "rounded-lg font-bold text-xs h-8 px-3 transition-all",
                viewMode === 'calendar' 
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 hover:text-white dark:bg-indigo-500 dark:hover:bg-indigo-600" 
                  : "text-slate-500 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              Calendar
            </Button>
          </div>
          <Button 
            onClick={() => setIsScheduleDialogOpen(true)} 
          >
            <Plus className="h-4 w-4 shrink-0 mr-1.5" />
            New Schedule
          </Button>
        </div>
      </div>

      {/* Calendar View Warning */}
      {viewMode === 'calendar' && !selectedSchedule && (
        <Card className="border border-slate-150 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardContent className="py-12 text-center text-slate-400 font-bold text-xs">
            <span>Please select a schedule from the list view to see the calendar.</span>
            <Button 
              variant="link" 
              onClick={() => setViewMode('list')} 
              className="text-[#143A62] dark:text-blue-400 font-bold text-xs ml-1 inline-flex items-center"
            >
              <span>Go to List View</span>
              <ExternalLink className="h-3.5 w-3.5 ml-1 shrink-0" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Schedules Grid (List View) */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {schedules.length === 0 ? (
            <Card className="col-span-full border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
              <CardContent className="py-12 text-center text-slate-400 font-semibold text-xs animate-pulse">
                No schedules found. Create your first schedule.
              </CardContent>
            </Card>
          ) : (
            schedules.map((schedule: any) => (
              <Card 
                key={schedule.id} 
                className={cn(
                  "cursor-pointer hover:shadow-lg transition-all rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden",
                  selectedSchedule === schedule.id 
                    ? 'ring-2 ring-[#143A62] dark:ring-blue-500 border-transparent shadow-md' 
                    : 'border-slate-100 dark:border-slate-800 shadow-sm'
                )}
                onClick={() => setSelectedSchedule(schedule.id)}
              >
                <CardHeader className="pb-3 px-6 pt-5">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{schedule.name}</CardTitle>
                    {schedule.is_published && getStatusBadge('Published')}
                  </div>
                </CardHeader>
                <CardContent className="px-6 pb-5 space-y-4">
                  <div className="flex items-center text-xs font-semibold text-slate-400">
                    <CalendarIcon className="h-4 w-4 mr-2 text-blue-550 shrink-0" />
                    <span>{new Date(schedule.start_date).toLocaleDateString()} - {new Date(schedule.end_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex space-x-2 mt-4 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSchedule(schedule.id);
                        openAssignmentDialog(schedule.id);
                      }}
                    >
                      <Users className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      Assign
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSchedule(schedule.id);
                        setViewMode('calendar');
                      }}
                    >
                      <LayoutGrid className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Assignments View (List or Calendar) */}
      {selectedSchedule && (
        viewMode === 'list' ? (
          assignments.length > 0 && (
            <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
              <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
                <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Assignments</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assignments.map((assignment: any) => (
                    <div key={assignment.id} className="flex items-center justify-between p-4 border border-slate-100 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/50 hover:shadow-sm transition-all">
                      <div>
                        <p className="font-bold text-xs text-[#143A62] dark:text-blue-400">{getEmployeeName(assignment.employee_id)}</p>
                        <p className="text-[10px] font-bold text-slate-400 mt-1">
                          {getShiftName(assignment.shift_id)} - {new Date(assignment.work_date).toLocaleDateString()}
                        </p>
                      </div>
                      {getStatusBadge(assignment.status || 'PENDING')}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
            <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Schedule Calendar</CardTitle>
              <Button 
                size="sm" 
                onClick={() => openAssignmentDialog(selectedSchedule)}
              >
                <Plus className="h-4 w-4 shrink-0 mr-1.5" />
                Add Assignment
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto custom-scrollbar">
              <div className="min-w-[800px]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-slate-150 dark:border-slate-800">
                      <th className="p-4 bg-slate-50/50 dark:bg-slate-800/20 text-left min-w-[180px] font-bold text-xs text-slate-400 tracking-wider">Employee</th>
                      {(() => {
                        const schedule = schedules.find((s: any) => s.id === selectedSchedule);
                        if (!schedule) return null;
                        const start = new Date(schedule.start_date);
                        const end = new Date(schedule.end_date);
                        const days = [];
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                          days.push(new Date(d));
                        }
                        return days.map((day) => (
                          <th key={day.toISOString()} className="p-4 bg-slate-50/50 dark:bg-slate-800/20 text-center min-w-[110px] font-bold text-xs text-slate-400 tracking-wider border-l border-slate-100 dark:border-slate-800">
                            <div className="font-bold text-xs text-[#143A62] dark:text-slate-200">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div className="text-[10px] text-slate-400 mt-0.5">{day.getDate()}</div>
                          </th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp: any) => (
                      <tr key={emp.id} className="hover:bg-slate-50/30 dark:hover:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800 transition-colors">
                        <td className="p-4 font-bold text-xs text-[#143A62] dark:text-blue-400">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-6.5 h-6.5 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[10px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                              {`${emp.first_name} ${emp.last_name || ''}`.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <span>{emp.first_name} {emp.last_name}</span>
                          </div>
                        </td>
                        {(() => {
                          const schedule = schedules.find((s: any) => s.id === selectedSchedule);
                          if (!schedule) return null;
                          const start = new Date(schedule.start_date);
                          const end = new Date(schedule.end_date);
                          const days = [];
                          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                            days.push(new Date(d));
                          }
                          return days.map((day) => {
                            const dateStr = day.toISOString().split('T')[0];
                            const assignment = assignments.find(
                              (a: any) => a.employee_id === emp.id && a.work_date.startsWith(dateStr)
                            );
                            return (
                              <td key={dateStr} className="p-4 text-center border-l border-slate-100 dark:border-slate-800">
                                {assignment ? (
                                  <div className="text-[10px] p-1.5 rounded-lg bg-blue-50 text-blue-805 dark:bg-blue-955/40 dark:text-blue-400 font-bold max-w-[120px] mx-auto truncate border border-blue-100/50">
                                    {getShiftName(assignment.shift_id)}
                                  </div>
                                ) : (
                                  <div className="text-xs text-slate-300 font-bold">-</div>
                                )}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Create Schedule Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Create Schedule</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Create a new shift schedule
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-bold text-slate-400 tracking-wider">Schedule Name *</Label>
              <Input
                id="name"
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                placeholder="e.g., Week 1 - January 2025"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-10 shadow-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">Start Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-bold text-xs h-10 rounded-xl border-slate-200 shadow-sm ${!startDate && 'text-muted-foreground'}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-blue-550 shrink-0" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border border-slate-100 shadow-xl rounded-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold text-slate-400 tracking-wider">End Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-bold text-xs h-10 rounded-xl border-slate-200 shadow-sm ${!endDate && 'text-muted-foreground'}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-blue-550 shrink-0" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border border-slate-100 shadow-xl rounded-xl" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                      disabled={(date) => startDate ? date < startDate : false}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setIsScheduleDialogOpen(false); resetScheduleForm(); }}
            >
              Cancel
            </Button>
            <Button onClick={handleScheduleSubmit}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Shift Dialog */}
      <Dialog open={isAssignmentDialogOpen} onOpenChange={setIsAssignmentDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Assign Shift</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Assign an employee to a shift
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="shift_id" className="text-xs font-bold text-slate-400 tracking-wider">Shift *</Label>
              <Select
                value={assignmentForm.shift_id}
                onValueChange={(value) => setAssignmentForm({ ...assignmentForm, shift_id: value })}
              >
                <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
                  <SelectValue placeholder="Select shift" />
                </SelectTrigger>
                <SelectContent className="border-slate-105 dark:border-slate-800">
                  {shifts.map((shift: any) => (
                    <SelectItem key={shift.id} value={shift.id} className="text-xs font-bold">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded" 
                          style={{ backgroundColor: shift.color || '#3B82F6' }}
                        ></div>
                        <span>{shift.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="employee_id" className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select
                value={assignmentForm.employee_id}
                onValueChange={(value) => setAssignmentForm({ ...assignmentForm, employee_id: value })}
              >
                <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="border-slate-105 dark:border-slate-800">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id} className="text-xs font-bold">
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-400 tracking-wider">Work Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-bold text-xs h-10 rounded-xl border-slate-200 shadow-sm ${!workDate && 'text-muted-foreground'}`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-blue-550 shrink-0" />
                    {workDate ? format(workDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 border border-slate-100 shadow-xl rounded-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={workDate}
                    onSelect={setWorkDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setIsAssignmentDialogOpen(false); resetAssignmentForm(); }}
            >
              Cancel
            </Button>
            <Button onClick={handleAssignmentSubmit}>
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftSchedules;